import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserClaims, refreshUserToken } from '../utils/adminUtils';

const DEPLOYED_AT = new Date().toLocaleString();

export default function AdminDashboard() {
  const { currentUser, isAdmin, userClaims } = useAuth();
  const [claims, setClaims] = useState({});
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [claimsError, setClaimsError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserClaims = async () => {
      try {
        const userClaims = await getUserClaims();
        setClaims(userClaims);
      } catch (error) {
        console.error('Error loading user claims:', error);
        setClaimsError((error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadUserClaims();
    }
  }, [currentUser]);

  useEffect(() => {
    const loadUsers = async () => {
      // TEMPORARILY DISABLED: isAdmin check commented out until functionality is stable
      if (!currentUser) return;
      setUsersLoading(true);
      setUsersError(null);
      try {
        const response = await fetch('/api/admin/users');
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load users');
        if (data.warning) throw new Error(`API warning: ${data.warning}`);
        setUsers(data.users || []);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsersError((error as Error).message);
      } finally {
        setUsersLoading(false);
      }
    };

    loadUsers();
  }, [currentUser, isAdmin]);

  const handleRefreshToken = async () => {
    setLoading(true);
    try {
      await refreshUserToken();
      const updatedClaims = await getUserClaims();
      setClaims(updatedClaims);
    } catch (error) {
      console.error('Error refreshing token:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const apiErrors = [
    claimsError && { label: 'Claims API', message: claimsError },
    usersError && { label: 'Users API', message: usersError },
  ].filter(Boolean) as { label: string; message: string }[];

  return (
    <div style={{ minHeight: '100vh', padding: '24px', paddingTop: '80px', fontFamily: 'sans-serif' }}>
      {/* Deployment proof banner — inline styles so global CSS cannot override */}
      <div
        style={{
          background: '#1d4ed8',
          color: '#fff',
          borderRadius: '8px',
          padding: '16px 24px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: '20px', fontWeight: 700 }}>MPD Admin Dashboard</div>
          <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px' }}>
            Page loaded: {DEPLOYED_AT}
          </div>
        </div>
        <div
          style={{
            background: '#16a34a',
            borderRadius: '6px',
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          LIVE
        </div>
      </div>

      {/* Visible API error log */}
      {apiErrors.length > 0 && (
        <div
          style={{
            background: '#fef2f2',
            border: '2px solid #dc2626',
            borderRadius: '8px',
            padding: '16px 24px',
            marginBottom: '20px',
          }}
        >
          <div style={{ color: '#dc2626', fontWeight: 700, fontSize: '15px', marginBottom: '10px' }}>
            API Errors ({apiErrors.length})
          </div>
          {apiErrors.map(err => (
            <div key={err.label} style={{ marginBottom: '8px' }}>
              <span
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  marginRight: '8px',
                }}
              >
                {err.label}
              </span>
              <span style={{ color: '#7f1d1d', fontSize: '13px', fontFamily: 'monospace' }}>
                {err.message}
              </span>
            </div>
          ))}
        </div>
      )}

    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="mt-1 text-sm text-gray-600">Administrative tools and user management</p>
          </div>

          <div className="p-6">
            {/* User Info Section */}
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Current User</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{currentUser?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">User ID</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono">{currentUser?.uid}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Admin Status</label>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        isAdmin ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {isAdmin ? 'Admin' : 'Regular User'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email Verified
                    </label>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        currentUser?.emailVerified
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {currentUser?.emailVerified ? 'Verified' : 'Not Verified'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Claims Section */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">Custom Claims & Roles</h2>
                <button
                  onClick={handleRefreshToken}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Refresh Claims
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                {Object.keys(claims).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(claims).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">{key}:</span>
                        <span className="text-sm text-gray-900">
                          {typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No custom claims found</p>
                )}
              </div>
            </div>

            {/* Admin Actions Section */}
            {/* TEMPORARILY DISABLED: isAdmin guard removed until functionality is stable */}
            {true && (
              <div className="mb-8">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Admin Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 md:col-span-2 lg:col-span-3">
                    <h3 className="text-sm font-medium text-blue-900">User Management</h3>
                    <p className="mt-1 text-xs text-blue-700">
                      Manage user accounts and permissions
                    </p>
                    {usersLoading ? (
                      <p className="mt-3 text-sm text-blue-700">Loading users...</p>
                    ) : usersError ? (
                      <p className="mt-3 text-sm text-red-600 break-words">
                        Could not load users: {usersError.split('\n')[0].slice(0, 160)}
                        {usersError.length > 160 && '...'}
                      </p>
                    ) : users.length === 0 ? (
                      <p className="mt-3 text-sm text-blue-700">No users found.</p>
                    ) : (
                      <div className="mt-3 overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 bg-white rounded-md">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                User ID
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Admin
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {users.map(user => (
                              <tr key={user.uid}>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  {user.displayName || user.email || '—'}
                                </td>
                                <td className="px-3 py-2 text-sm font-mono text-gray-600 break-all">
                                  {user.uid}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      user.isAdmin
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {user.isAdmin ? 'Yes' : 'No'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-green-900">System Settings</h3>
                    <p className="mt-1 text-xs text-green-700">Configure application settings</p>
                    <button className="mt-3 w-full bg-green-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-green-700">
                      Settings
                    </button>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-indigo-900">Coupon Management</h3>
                    <p className="mt-1 text-xs text-indigo-700">
                      Manage discounts and referral attribution
                    </p>
                    <Link
                      to="/coupon-admin"
                      className="mt-3 block w-full bg-indigo-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-indigo-700 text-center"
                    >
                      Manage Coupons
                    </Link>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-purple-900">Analytics</h3>
                    <p className="mt-1 text-xs text-purple-700">View application analytics</p>
                    <button className="mt-3 w-full bg-purple-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-purple-700">
                      View Analytics
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Instructions Section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-yellow-800">Setup Instructions</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p className="mb-2">
                  To set admin roles, you need to use Firebase Admin SDK on your backend:
                </p>
                <ol className="list-decimal list-inside space-y-1 ml-4">
                  <li>Install Firebase Admin SDK in your backend</li>
                  <li>
                    Use admin.auth().setCustomUserClaims(uid, {`{admin: true}`}) to set admin role
                  </li>
                  <li>User will need to refresh their token or re-login to see changes</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
